import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars,
  faXmark,
  faUserCircle,
  faGear,
  faRightFromBracket,
  faUserGroup,
  faHome,
  faBuilding,
  faCar,
  faWandMagicSparkles,
  faPaw,
  faUsers,
  faBox,
  faLayerGroup,
  faWater,
  faClipboardList,
  faBullhorn,
  faTriangleExclamation,
  faCommentDots
} from '@fortawesome/free-solid-svg-icons';
import closeSession from '../../utils/closeSession';
import { BASE_URL } from '../../utils/constants';
import NotificationBell from '../../common/NotificationBell';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userAvatar, setUserAvatar] = useState(null);
  const [userName, setUserName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const userData = JSON.parse(atob(token.split('.')[1]));
        setUserAvatar(userData.avatar);
        setUserName(`${userData.nombre} ${userData.apellido}`);
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }

    // Escuchar cambios en el localStorage
    const handleStorageChange = () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userData = JSON.parse(atob(token.split('.')[1]));
          setUserAvatar(userData.avatar);
          setUserName(`${userData.nombre} ${userData.apellido}`);
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Scroll to top on route change so pages always show from top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Keep page content from being overlapped by the fixed sidebar
  useEffect(() => {
    const padding = isOpen ? '16rem' : '4rem';
    const prev = document.body.style.paddingLeft;
    document.body.style.paddingLeft = padding;
    return () => { document.body.style.paddingLeft = prev; };
  }, [isOpen]);

  const handleListItemClick = (text) => {
    const routes = {
      'usuarios': '/adminUsers',
      'Apartamentos': '/adminApartments',
      'Torres': '/adminTowers',
      'Vehiculos': '/adminVehicles',
      'Parqueaderos': '/adminParkings',
      'Parq. Visitantes': '/adminVisitorParking',
      'Mascotas': '/adminPets',
      'Reporte Visitas': '/visitReport',
      'Reporte Paquetería': '/shipmentReport',
      'Piscina': '/adminPool',
      'Salón Comunal': '/adminSalon',
      'Comunicados': '/adminComunicados',
      'Emergencias': '/adminEmergencias',
      'WhatsApp': '/adminWhatsapp'
    };
    
    if (routes[text]) {
      navigate(routes[text]);
    }
    setIsOpen(false);
  };

  const handleLogout = () => {
    closeSession();
  };

  const menuItems = [
    { text: 'usuarios', icon: faUserGroup },
    { text: 'Apartamentos', icon: faHome },
    { text: 'Torres', icon: faBuilding },
    { text: 'Vehiculos', icon: faCar },
    { text: 'Parqueaderos', icon: faLayerGroup },
    { text: 'Parq. Visitantes', icon: faBuilding },
    { text: 'Mascotas', icon: faPaw }
  ];

  const reportItems = [
    { text: 'Reporte Visitas', icon: faUsers },
    { text: 'Reporte Paquetería', icon: faBox }
  ];

  const solicitudItems = [
    { text: 'Piscina', icon: faWater },
    { text: 'Salón Comunal', icon: faClipboardList }
  ];

  const comunicadosItems = [
    { text: 'Comunicados', icon: faBullhorn }
  ];

  const emergenciasItems = [
    { text: 'Emergencias', icon: faTriangleExclamation }
  ];

  const configuracionItems = [
    { text: 'WhatsApp', icon: faCommentDots }
  ];

  return (
    <>
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-bg shadow-lg nav-safe-top">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-white hover:bg-primary-700 p-2 rounded-lg transition-colors duration-200"
              >
                {isOpen ? (
                  <FontAwesomeIcon icon={faXmark} className="h-6 w-6" />
                ) : (
                  <FontAwesomeIcon icon={faBars} className="h-6 w-6" />
                )}
              </button>
              <h1 
                className="ml-4 text-xl font-bold text-white cursor-pointer hover:text-primary-100 transition-colors"
                onClick={() => navigate('/dashboard')}
              >
                VECINO SEGURO
              </h1>
            </div>

            {/* Actions: Bell + Profile */}
            <div className="flex items-center gap-2">
              <NotificationBell />

              {/* Profile Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 text-white hover:bg-primary-700 px-3 py-2 rounded-lg transition-colors duration-200"
                >
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center overflow-hidden ring-2 ring-white">
                  {userAvatar ? (
                    <img 
                      src={`${BASE_URL}${userAvatar}`} 
                      alt="Avatar" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FontAwesomeIcon icon={faUserCircle} className="h-6 w-6 text-primary-600" />
                  )}
                </div>
                <span className="hidden md:block font-medium">{userName}</span>
              </button>

              {showProfileMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)}></div>
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-50 overflow-hidden">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          navigate('/profile');
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <FontAwesomeIcon icon={faGear} className="h-5 w-5 text-primary-600 mr-3" />
                        Configuración
                      </button>
                      <div className="border-t border-gray-100"></div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <FontAwesomeIcon icon={faRightFromBracket} className="h-5 w-5 mr-3" />
                        Salir
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        </div>
      </nav>

      {/* Sidebar (collapsible: icons visible when collapsed) */}
      <div className={`fixed sidebar-top-safe left-0 bottom-0 z-40 bg-white shadow-xl transition-all duration-300 ${isOpen ? 'w-64' : 'w-16'} overflow-hidden`}>
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200">
            {isOpen ? (
              <h2 className="text-lg font-semibold text-gray-800">ADMINISTRACION</h2>
            ) : (
              <div className="flex items-center justify-center">
                <span className="text-primary-600 font-bold">A</span>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="flex-1 overflow-y-auto py-4">
            <nav className="space-y-1 px-3">
              {menuItems.map((item) => (
                  <button
                    key={item.text}
                    onClick={() => handleListItemClick(item.text)}
                    className="flex items-center w-full px-3 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-all duration-200 group"
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-primary-600 group-hover:text-primary-700" />
                    {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                  </button>
                )
              )}
            </nav>

            <div className="my-4 border-t border-gray-200"></div>

            {/* Report Items */}
            <nav className="space-y-1 px-3">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                  {isOpen ? (
                    'Reportes'
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="text-gray-500 font-semibold">R</span>
                    </div>
                  )}
                </div>
              {reportItems.map((item) => (
                  <button
                    key={item.text}
                    onClick={() => handleListItemClick(item.text)}
                    className="flex items-center w-full px-3 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-all duration-200 group"
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-primary-600 group-hover:text-primary-700" />
                    {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                  </button>
                )
              )}
            </nav>

            <div className="my-4 border-t border-gray-200"></div>

            {/* Solicitudes Items */}
            <nav className="space-y-1 px-3">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                  {isOpen ? (
                    'Solicitudes'
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="text-gray-500 font-semibold">S</span>
                    </div>
                  )}
                </div>
              {solicitudItems.map((item) => (
                  <button
                    key={item.text}
                    onClick={() => handleListItemClick(item.text)}
                    className="flex items-center w-full px-3 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-all duration-200 group"
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-primary-600 group-hover:text-primary-700" />
                    {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                  </button>
                )
              )}
            </nav>
            <div className="my-4 border-t border-gray-200"></div>

            {/* Comunicados */}
            <nav className="space-y-1 px-3">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                  {isOpen ? (
                    'Comunicados'
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="text-gray-500 font-semibold">C</span>
                    </div>
                  )}
                </div>
              {comunicadosItems.map((item) => (
                  <button
                    key={item.text}
                    onClick={() => handleListItemClick(item.text)}
                    className="flex items-center w-full px-3 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-all duration-200 group"
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-primary-600 group-hover:text-primary-700" />
                    {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                  </button>
                )
              )}
            </nav>

            <div className="my-4 border-t border-gray-200"></div>

            {/* Emergencias */}
            <nav className="space-y-1 px-3">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                  {isOpen ? 'Emergencias' : (
                    <div className="flex items-center justify-center">
                      <span className="text-gray-500 font-semibold">E</span>
                    </div>
                  )}
                </div>
              {emergenciasItems.map((item) => (
                  <button
                    key={item.text}
                    onClick={() => handleListItemClick(item.text)}
                    className="flex items-center w-full px-3 py-3 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-200 group"
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                    {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                  </button>
                )
              )}
            </nav>

            <div className="my-4 border-t border-gray-200"></div>

            {/* Configuración */}
            <nav className="space-y-1 px-3">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                  {isOpen ? 'Configuración' : (
                    <div className="flex items-center justify-center">
                      <span className="text-gray-500 font-semibold">C</span>
                    </div>
                  )}
                </div>
              {configuracionItems.map((item) => (
                  <button
                    key={item.text}
                    onClick={() => handleListItemClick(item.text)}
                    className="flex items-center w-full px-3 py-3 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-all duration-200 group"
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                    {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                  </button>
                )
              )}
            </nav>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 sidebar-top-safe"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      {/* Spacer for top navbar */}
      <div className="h-nav-safe"></div>
    </>
  );
}